import { useMemo, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { Box, IconButton, Tooltip, HStack, useToast } from "@chakra-ui/react";
import { HiEye, HiDocumentDuplicate } from "react-icons/hi";
import { useNavigate } from "react-router-dom";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";

export default function CodifyDefinitionsGrid({
  codifyDefinitions,
  loading,
  onCopy,
  selectedCollection = null
}) {
  const navigate = useNavigate();
  const toast = useToast();
  const [copying, setCopying] = useState(null);

  const handleCopy = async (definition) => {
    setCopying(definition._id);
    try {
      // First, fetch the full definition with all prompts and data definitions
      const fetchResponse = await fetch(
        `https://codify.poc.mrc.ai/api/definitions/${definition._id}`
      );

      if (!fetchResponse.ok) {
        throw new Error("Failed to fetch full definition");
      }

      const fullDefinition = await fetchResponse.json();

      // Create a deep copy of the full definition with a new ID and updated metadata
      const copiedDefinition = {
        ...fullDefinition,
        _id: crypto.randomUUID(),
        type: `${fullDefinition.type} (Copy)`,
        createdDate: new Date().toISOString(),
        // Ensure all nested structures are copied
        dataDefinitions: fullDefinition.dataDefinitions ? [...fullDefinition.dataDefinitions] : [],
        prompts: fullDefinition.prompts
          ? fullDefinition.prompts.map(prompt => ({
              ...prompt,
              dataDefinitions: prompt.dataDefinitions ? [...prompt.dataDefinitions] : []
            }))
          : [],
      };

      // Save the new definition to the API
      const response = await fetch("https://codify.poc.mrc.ai/api/definitions", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(copiedDefinition),
      });

      if (!response.ok) {
        throw new Error("Failed to copy definition");
      }

      const result = await response.json();

      toast({
        title: "Definition copied",
        description: `Successfully copied "${fullDefinition.type}"`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      // Navigate to the new definition
      navigate(`/codifydefinitions/${result._id}`);

      // Call the onCopy callback if provided
      if (onCopy) {
        onCopy(result);
      }
    } catch (error) {
      console.error("Error copying definition:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to copy definition",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setCopying(null);
    }
  };

  const ActionsCellRenderer = useMemo(
    () => (props) => {
      const isCopying = copying === props.data._id;
      return (
        <HStack spacing={2} height="100%" alignItems="center">
          <Tooltip label="View Codify Definition">
            <IconButton
              icon={<HiEye />}
              size="sm"
              aria-label="View definition"
              onClick={() => navigate(`/codifydefinitions/${props.data._id}`)}
            />
          </Tooltip>
          <Tooltip label="Copy Codify Definition">
            <IconButton
              icon={<HiDocumentDuplicate />}
              size="sm"
              aria-label="Copy definition"
              onClick={() => handleCopy(props.data)}
              isLoading={isCopying}
              disabled={isCopying}
            />
          </Tooltip>
        </HStack>
      );
    },
    [navigate, copying]
  );

  // Conditionally enable row grouping only when no collection is selected
  const columnDefs = useMemo(
    () => [
      {
        field: "collection",
        headerName: "Collection",
        filter: "agTextColumnFilter",
        rowGroup: selectedCollection === null, // Only group when viewing all
        hide: selectedCollection !== null, // Hide column when filtered by collection
        sort: "asc",
        width: 150,
      },
      {
        headerName: "Actions",
        cellRenderer: ActionsCellRenderer,
        width: 120,
        sortable: false,
        filter: false,
      },
      {
        field: "type",
        headerName: "Type",
        filter: "agTextColumnFilter",
        flex: 1,
        minWidth: 200,
      },
      {
        field: "description",
        headerName: "Description",
        filter: "agTextColumnFilter",
        flex: 2,
        minWidth: 300,
      },
    ],
    [ActionsCellRenderer, selectedCollection]
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      resizable: true,
      filter: true,
    }),
    []
  );

  const autoGroupColumnDef = useMemo(
    () => ({
      headerName: "Collection",
      minWidth: 250,
      cellRendererParams: {
        suppressCount: false,
      },
    }),
    []
  );

  return (
    <Box
      height="100%"
      width="100%"
      className="ag-theme-alpine"
    >
      <AgGridReact
        rowData={codifyDefinitions}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        autoGroupColumnDef={autoGroupColumnDef}
        groupDefaultExpanded={1}
        animateRows={true}
        pagination={true}
        paginationPageSize={20}
        loading={loading}
        rowHeight={35}
        headerHeight={40}
      />
    </Box>
  );
}
